import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Flex,
  Button,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Text,
  Image,
  useToast,
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import OrgLogo from "assets/img/ORG.png";
import SconexLogo from "assets/img/sconex.png";
import { login } from "services/authService";
import { getVideoSource } from "services/videoService";
import { LOGIN_BACKGROUND_VIDEO } from "constants/videoAssets";
import "../../styles/auth/SignIn.css";

function SignIn() {
  const toast = useToast();
  const history = useHistory();
  const primaryTextColor = "#f8fafc";
  const secondaryTextColor = "rgba(226, 232, 240, 0.85)";
  const focusColor = "#7dd3fc";

  const [formData, setFormData] = useState({
    userName: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadBackground() {
      try {
        const url = await getVideoSource(LOGIN_BACKGROUND_VIDEO);
        if (isMounted) {
          setBackgroundUrl(url);
          setIsVideoReady(true);
        }
      } catch (error) {
        console.error("[SIGN IN] Failed to load background video", error);
        setIsVideoReady(false);
      }
    }

    loadBackground();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.userName) newErrors.userName = "Username is required";
    if (!formData.password) newErrors.password = "Password is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await login({
        userName: formData.userName,
        password: formData.password,
      });

      const { token, id } = response.data;

      if (formData.rememberMe) {
        localStorage.setItem("authToken", token);
        localStorage.setItem("userName", formData.userName);
        // localStorage.setItem("email", email);
        localStorage.setItem("userId", id);
      } else {
        sessionStorage.setItem("authToken", token);
        sessionStorage.setItem("userName", formData.userName);
        // sessionStorage.setItem("email", email);
        sessionStorage.setItem("userId", id);
      }

      history.push("/admin/dashboard");
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to sign in. Please check your credentials.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  return (
    <Flex className="signin-shell">
      {backgroundUrl && (
        <video
          className={`signin-bg-video ${isVideoReady ? "is-visible" : ""}`}
          src={backgroundUrl}
          autoPlay
          muted
          loop
          playsInline
        />
      )}
      <Box className="signin-hero">
        <Box className="hero-content">
          <Flex justify="center" align="center" mb="60px">
            <Image
              src={SconexLogo}
              alt="Sconex Logo"
              className="hero-logo"
              draggable="false"
            />
          </Flex>
          <Box position="relative" height="120px">
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite"
            >
              Welcome to ORGI Dashboard
            </Text>
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite 4s"
            >
              Explore Soft Drinks Data
            </Text>
            <Text
              fontSize={{ base: "2xl", lg: "3xl" }}
              fontWeight="bold"
              color="white"
              position="absolute"
              width="100%"
              opacity={0}
              animation="heroFade 12s infinite 8s"
            >
              Image to Insight – Instantly.
            </Text>
          </Box>
          <Text fontSize="lg" color="rgba(226, 232, 240, 0.92)" mt={8} lineHeight="tall">
            Discover detailed reports, vibrant visuals, and smart analysis all in
            one intelligent dashboard.
          </Text>
        </Box>
      </Box>

      <Box className="signin-panel" color={primaryTextColor}>
        <Flex className="signin-content" align="center" justify="center">
          <Box className="signin-card">
            <Box className="card-shimmer" />
            <Stack align="center" spacing={3} mb={6}>
              <Image
                src={OrgLogo}
                alt="ORG Logo"
                className="signin-logo"
                draggable="false"
              />
              <Text fontSize="2xl" fontWeight="bold" color={primaryTextColor}>
                Sign in your account
              </Text>
              <Text
                fontSize="md"
                textAlign="center"
                color={secondaryTextColor}
                px={4}
              >
                Image to insight—instantly. Step back into your intelligent
                dashboard.
              </Text>
            </Stack>

            <form onSubmit={handleSubmit}>
              <FormControl mb={5}>
                <FormLabel color={secondaryTextColor} fontWeight="600">
                  Username
                </FormLabel>
                <Input
                  name="userName"
                  type="text"
                  placeholder="Enter your username"
                  value={formData.userName}
                  onChange={handleChange}
                  className="signin-input"
                  _focus={{
                    borderColor: focusColor,
                    boxShadow: `0 0 0 1px ${focusColor}`,
                  }}
                />
                {errors.userName && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {errors.userName}
                  </Text>
                )}
              </FormControl>

              <FormControl mb={5}>
                <FormLabel color={secondaryTextColor} fontWeight="600">
                  Password
                </FormLabel>
                <Input
                  name="password"
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className="signin-input"
                  _focus={{
                    borderColor: focusColor,
                    boxShadow: `0 0 0 1px ${focusColor}`,
                  }}
                />
                {errors.password && (
                  <Text color="red.500" fontSize="sm" mt={2}>
                    {errors.password}
                  </Text>
                )}
              </FormControl>

              <FormControl display="flex" alignItems="center" mb={8}>
                <Switch
                  id="rememberMe"
                  name="rememberMe"
                  isChecked={formData.rememberMe}
                  onChange={handleChange}
                  colorScheme="purple"
                  mr={3}
                />
                <FormLabel
                  htmlFor="rememberMe"
                  mb="0"
                  fontWeight="600"
                  color={primaryTextColor}
                >
                  Remember me
                </FormLabel>
              </FormControl>

              <Button
                type="submit"
                width="100%"
                className="signin-button"
                py={6}
                bgGradient="linear(140deg, #111111 0%, #000000 55%, #1a1a1a 100%)"
                color="white"
                _hover={{
                  bgGradient:
                    "linear(140deg, #050505 0%, #000000 60%, #111111 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 25px 45px rgba(0, 0, 0, 0.45)",
                }}
                _active={{
                  bgGradient:
                    "linear(140deg, #050505 0%, #0a0a0a 55%, #111111 100%)",
                  transform: "translateY(0)",
                  boxShadow: "0 15px 30px rgba(0, 0, 0, 0.35)",
                }}
              >
                Sign In
              </Button>
            </form>
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
}

export default SignIn;
